/** Product app login route — always same-origin, never a marketing absolute URL. */
export const PRODUCT_LOGIN_PATH = "/login";

function decodePathname(pathname) {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
}

/**
 * Detect paths like /https://test.commray.com/login (marketing URL concatenated onto app host).
 */
export function isMalformedAppPath(pathname, hostname = "") {
  if (!pathname || pathname === PRODUCT_LOGIN_PATH || pathname === "/signup") {
    return false;
  }

  const path = decodePathname(pathname);

  if (path.includes("://") || path.includes("%3A%2F%2F") || path.includes("%3a%2f%2f")) {
    return true;
  }

  if (/^\/https?(?:[:%]|\/)/i.test(path)) {
    return true;
  }

  // Product app should never have marketing hostnames in the path segment.
  if (hostname.startsWith("app.") && /commray\.com/i.test(path)) {
    return true;
  }

  return false;
}

/**
 * Fix malformed URLs before React Router can treat them as relative paths.
 * Returns true if a redirect was triggered (page will unload).
 */
export function sanitizeAuthLocation() {
  if (typeof window === "undefined") return false;

  const { pathname, search, hash, hostname } = window.location;
  if (!isMalformedAppPath(pathname, hostname)) {
    return false;
  }

  window.location.replace(`${PRODUCT_LOGIN_PATH}${search}${hash}`);
  return true;
}

export function isAbsoluteUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

/** Navigate to login on the product app (never an external marketing URL). */
export function goToLogin(navigate) {
  if (typeof navigate === "function") {
    navigate(PRODUCT_LOGIN_PATH, { replace: true });
    return;
  }
  window.location.replace(PRODUCT_LOGIN_PATH);
}

function isProductAppHostname(urlString) {
  try {
    const url = new URL(urlString);
    const host = window.location.hostname;
    return url.hostname === host || url.hostname.startsWith("app.");
  } catch {
    return false;
  }
}

/** Follow backend redirect fields safely (never append absolute URLs as relative paths). */
export function followRedirectTarget(target, navigate) {
  const value = target == null ? "" : String(target).trim();
  if (!value) {
    goToLogin(navigate);
    return;
  }

  if (isAbsoluteUrl(value)) {
    if (isProductAppHostname(value)) {
      window.location.assign(value);
    } else {
      // Marketing or unknown absolute URL — send to product login on this host.
      goToLogin(navigate);
    }
    return;
  }

  const path = value.startsWith("/") ? value : `/${value}`;
  if (typeof navigate === "function") {
    navigate(path, { replace: true });
    return;
  }
  window.location.assign(path);
}
